import { Pin as LucidePin, PencilLine, Trash2 } from 'lucide-react';
import { FC, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './ConversationContextMenu.scss';

interface ConversationContextMenuProps {
    conversationId: string;
    isPinned: boolean;
    position: { top: number; left: number };
    onEdit: (id: string) => void;
    onPinToggle: (id: string, pinned: boolean) => void;
    onDelete: (id: string) => void;
    onClose: () => void;
    triggerRef?: React.RefObject<HTMLElement>; // Ref of the button that opened the menu
}
export const ConversationContextMenu: FC<ConversationContextMenuProps> = (props) => {
    const { conversationId, isPinned, position, onEdit, onPinToggle, onDelete, onClose, triggerRef } = props;
    const { t } = useTranslation();
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            // Don't close if clicking on the menu itself or on the button that opened it
            if (menuRef.current && !menuRef.current.contains(target) &&
                (!triggerRef?.current || !triggerRef.current.contains(target))) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose, triggerRef]);

        const style: React.CSSProperties = {
            // expose top/left as CSS variables so layout remains in the stylesheet
            ['--menu-top' as any]: position?.top ? `${position.top}px` : undefined,
            ['--menu-left' as any]: position?.left ? `${position.left}px` : undefined,
        };

        return createPortal(
            <div
                ref={menuRef}
                className="conversation-context-menu"
                role="menu"
                aria-orientation="vertical"
                style={style}
            >
            <div
                className="conversation-context-menu__item"
                role="menuitem"
                onClick={(ev) => {
                    ev.stopPropagation();
                    onEdit(conversationId);
                    onClose();
                }}
            >
                <PencilLine size={16} />
                <span>{t('Rename') || 'Rename'}</span>
            </div>

            <div
                className="conversation-context-menu__item"
                role="menuitem"
                onClick={(ev) => {
                    ev.stopPropagation();
                    onPinToggle(conversationId, !isPinned);
                    onClose();
                }}
            >
                <LucidePin size={16} />
                <span>{isPinned ? (t('Unpin conversation') || 'Unpin conversation') : (t('Pin conversation') || 'Pin conversation')}</span>
            </div>

            <div
                className="conversation-context-menu__item conversation-context-menu__item--delete"
                role="menuitem"
                onClick={(ev) => {
                    ev.stopPropagation();
                    onDelete(conversationId);
                    onClose();
                }}
            >
                <Trash2 size={16} />
                <span>{t('Delete conversation') || 'Delete conversation'}</span>
            </div>
        </div>,
        document.body
    );
};
